import Highcharts from 'highcharts';
import debounce from 'lodash/debounce';
import sum from 'lodash/sum';
import {Component} from 'panel';
import ResizeObserver from 'resize-observer-polyfill';

import {abbreviateNumber3SigFigs} from 'common/util';
import {truncateMiddle} from 'common/util/string';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {getChartSegmentColorCombo} from 'common/util/colors';
import mpFonts from 'common/stylesheets/mixins/fonts.json';

import {ChartDataItem, TooltipFormatter, TooltipFormatterParams} from './types';
import {defaultTooltipFormatter} from './util';
import css from './index.styl?inline';
import template from './index.jade';

interface PieChartJsxProps {
  attrs?: {
    elref: string;
  };
  props: {
    chartData: ChartDataItem[];
    tooltipFormatter?: TooltipFormatter;
  };
}

export class PieChart extends Component<EmptyObj, PieChartJsxProps['attrs']> {
  public get config() {
    return {
      css,
      useShadowDom: true,
      template,
    };
  }

  public attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === `disable-animation`) {
      this.updateChart();
    }
  }

  public connectedCallback() {
    super.connectedCallback();

    if (!this.chartData) {
      throw new Error(`"chartData" is a required component prop`);
    }

    const debouncedUpdateChart = debounce(this.updateChart.bind(this), 20, {trailing: true});
    this.selfResizeObserver = new ResizeObserver((entries) => {
      const {height} = entries[0].contentRect;
      this.chartHeight = height;
      // update the entire chart instead of only reflow(), because some chart options may be based on chartHeight
      debouncedUpdateChart();
    });
    this.selfResizeObserver.observe(this);

    const chartEl = document.createElement(`div`);
    chartEl.style.width = `100%`;
    chartEl.style.height = `100%`;
    this.el.appendChild(chartEl);

    this.highcharts = new Highcharts.Chart(chartEl, this.getChartOptions());
    this.updateChart();
  }

  public disconnectedCallback() {
    this.selfResizeObserver.disconnect();

    if (this.highcharts) {
      this.highcharts.destroy();
      delete this.highcharts;
    }
    super.disconnectedCallback();
  }

  public tooltipFormatter: TooltipFormatter;

  private _chartData: ChartDataItem[] = [];
  public get chartData() {
    return this._chartData;
  }
  public set chartData(data: ChartDataItem[]) {
    this._chartData = data;

    this.total = sum(this.chartData.map((item) => item.value));
    this.highchartsSeriesData = this.chartData.map((item, idx) => {
      return {
        ...item,
        value: +item.value,
        color: item.color || getChartSegmentColorCombo(idx, `pieChart`).primary, // assign default primary colors if there's none
        percent: item.percent,
        secondaryColor: item.secondaryColor || getChartSegmentColorCombo(idx, `pieChart`).secondary, // assign default secondary colors if there's none
        name: item.name,
        y: item.pieChartValue || item.value,
      };
    });
    this.updateChart();
  }

  // only public because tests may need to access it
  public highcharts: TODO;

  private selfResizeObserver: ResizeObserver;
  private total = 0;
  private highchartsSeriesData: any[] = [];
  private chartHeight: number; // px

  private createTooltipFormatter(): TooltipFormatter {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const component = this;
    return function (this: TODO) {
      const params: TooltipFormatterParams = {
        name: this.key,
        value: this.point.y,
        percent: this.point.percentage,
        color: this.point.color,
        point: this.point,
      };
      return component.tooltipFormatter ? component.tooltipFormatter(params) : defaultTooltipFormatter(params);
    };
  }

  private getChartOptions() {
    const isSmall = this.chartHeight <= 300;

    return {
      chart: {
        animation: false,
        backgroundColor: null,
        // "reflow" is normally true by default, but it only triggers on window resize events,
        // so we disable it and call reflow() manually with a ResizeObserver on this element.
        reflow: false,
        type: `pie`,
        style: {
          fontFamily: mpFonts.fontFamilyPrimary,
        },
        margin: isSmall ? 8 : 64,
      },
      credits: {
        enabled: false,
      },
      title: {
        align: `center`,
        style: {
          color: null,
          fill: null,
          fontSize: isSmall ? `19px` : false,
        },
        text: abbreviateNumber3SigFigs(this.total),
        verticalAlign: `middle`,
        y: 10,
      },
      plotOptions: {
        pie: {
          center: [`50%`, `50%`],
          dataLabels: {
            style: {
              color: null,
              fill: null,
              fontSize: false,
              textOutline: `none`,
            },
            borderColor: false,
            color: null,
            enabled: !isSmall,
            formatter(this: TODO) {
              const label = this.point.options.displayName ?? this.key;
              return `
                <div class="pie-segment-label">
                  ${truncateMiddle(label, 30)}
                </div>
              `;
            },
          },
          shadow: false,
          size: `100%`,
          slicedOffset: 0,
        },
        series: {
          animation: false,
        },
      },
      tooltip: {
        backgroundColor: null,
        padding: 0,
        borderRadius: 0,
        borderWidth: 0,
        formatter: this.createTooltipFormatter(),
        hideDelay: 0,
        shadow: false,
        useHTML: true,
      },
      series: [
        {
          data: this.highchartsSeriesData,
          innerSize: `60%`,
        },
      ],
    };
  }

  private updateChart() {
    if (this.highcharts) {
      this.highcharts.update(
        this.getChartOptions(),
        true, // redraw after update
        true, // oneToOne: add/remove series when they change
      );
      this.highcharts.reflow();
    }
  }
}

export const PieChartEl = defineAndMakeJsxFactory<PieChartJsxProps>(`mp-pie-chart`, PieChart);
